import { notifyGamificationRewardOutcomes } from "@app/gamification/show-reward-outcome";
import type { VideoSegmentTrackingResponse } from "@core/activities/types";
import { useLog } from "@core/shared/hooks/use-log.hook";
import type { Resource } from "@core/shared/types";
import { useAction } from "@framework/shared/hooks/useAction";
import { useCallback, useEffect, useRef } from "react";
import { compressActivityPayload } from "../../utils/payload-compression";
import { SegmentTracker, type VideoSegment } from "../../utils/segment-tracker";

const SEGMENT_FLUSH_INTERVAL = 15_000;

type UseVideoSegmentsReturn = {
  trackVideoSegments: (segments: VideoSegment[]) => Promise<void>;
  flushSegments: () => VideoSegment[];
  getTotalWatchedTime: () => number;
  getCompletionPercentage: (videoDuration: number) => number;
  getCurrentSegmentInfo: () => VideoSegment | null;
  // Event handlers for VideoPlayer2
  onTimeUpdate: (currentTime: number) => void;
  onSeeking: (fromTime: number, toTime: number) => void;
  onSeeked: (toTime: number) => void;
  onPlay: (currentTime: number) => void;
  onPause: (currentTime: number) => void;
  onStart: (currentTime: number) => void;
  onEnd: () => void;
};

export function useVideoSegments(
  resource: Resource,
  playerRef?: React.RefObject<any>,
): UseVideoSegmentsReturn {
  const submit = useAction<VideoSegmentTrackingResponse>(
    "/activities/resources",
  );
  const segmentTrackerRef = useRef<SegmentTracker | null>(null);
  const isVideoPlayingRef = useRef<boolean>(false);
  const hasUserInteractedRef = useRef<boolean>(false);
  const { log } = useLog(resource.id);

  const trackVideoSegments = useCallback(
    async (segments: VideoSegment[]) => {
      if (!segments.length) return;

      log(
        `Tracking ${segments.length} segments`,
        segments.map((s) => ({
          start: s.start.toFixed(2),
          end: s.end.toFixed(2),
          duration: s.duration.toFixed(2),
          complete: s.isComplete,
          reason: s.reason,
        })),
        "info",
        "useVideoSegments",
      );

      try {
        const payload = {
          type: "segment",
          resource: resource.id,
          data: {
            segments: segments.map((segment) => ({
              start: segment.start,
              end: segment.end,
              duration: segment.duration,
              quality: segment.quality,
              speed: segment.speed,
            })),
          },
        };

        const compressedPayload = compressActivityPayload(payload);
        log(
          "Sending segment batch",
          compressedPayload,
          "debug",
          "useVideoSegments",
        );
        const response = await submit(compressedPayload);

        if (response.isOK) {
          notifyGamificationRewardOutcomes(response.data.rewardOutcome);
        }
      } catch (error) {
        log(
          "Failed to track video segments",
          { error: error instanceof Error ? error.message : error },
          "error",
          "useVideoSegments",
        );
        // Don't throw error to avoid breaking user experience
      }
    },
    [resource.id, submit, log],
  );

  // Initialize segment tracker
  useEffect(() => {
    segmentTrackerRef.current = new SegmentTracker({
      segmentSize: 5, // 5 seconds
      maxSegments: 100,
      flushInterval: SEGMENT_FLUSH_INTERVAL,
      logger: log,
      getCurrentQuality: () => {
        const player = playerRef?.current;
        return player?.currentQualityLevel?.label || undefined;
      },
      getCurrentSpeed: () => {
        const player = playerRef?.current;
        return player?.speed || undefined;
      },
    });

    // Start auto-flush
    segmentTrackerRef.current.startAutoFlush((segments) => {
      // Only track segments that have some duration
      const validSegments = segments.filter((segment) => segment.duration > 0);
      if (validSegments.length > 0) {
        log(
          `Auto-flushing ${validSegments.length} segments`,
          { segments: validSegments },
          "info",
          "useVideoSegments",
        );
        trackVideoSegments(validSegments);
      }
    });

    return () => {
      if (segmentTrackerRef.current) {
        segmentTrackerRef.current.stopAutoFlush();
        const remainingSegments = segmentTrackerRef.current
          .flushSegments()
          .filter((segment) => segment.duration > 0);
        if (remainingSegments.length) {
          void trackVideoSegments(remainingSegments);
        }
        segmentTrackerRef.current.reset();
      }
    };
  }, [resource.id, trackVideoSegments, log, playerRef]);

  const flushSegments = useCallback((): VideoSegment[] => {
    if (!segmentTrackerRef.current) return [];
    return segmentTrackerRef.current.flushSegments();
  }, []);

  const getTotalWatchedTime = useCallback((): number => {
    if (!segmentTrackerRef.current) return 0;
    return segmentTrackerRef.current.getTotalWatchedTime();
  }, []);

  const getCompletionPercentage = useCallback(
    (videoDuration: number): number => {
      if (!segmentTrackerRef.current) return 0;
      return segmentTrackerRef.current.getCompletionPercentage(videoDuration);
    },
    [],
  );

  const getCurrentSegmentInfo = useCallback((): VideoSegment | null => {
    if (!segmentTrackerRef.current) return null;
    return segmentTrackerRef.current.getCurrentSegmentInfo();
  }, []);

  // Event handlers
  const onTimeUpdate = useCallback(
    (currentTime: number) => {
      if (!segmentTrackerRef.current) {
        log(
          `Hook: onTimeUpdate called but segmentTrackerRef.current is null at time ${currentTime.toFixed(2)}s`,
          null,
          "warn",
          "useVideoSegments",
        );
        return;
      }

      // Only track segments when video is actually playing
      if (!isVideoPlayingRef.current) {
        // Log the first few timeupdate calls when not playing to understand the sequence
        if (currentTime <= 1) {
          log(
            `onTimeUpdate called but video not playing at ${currentTime.toFixed(2)}s`,
            {
              currentTime,
              isVideoPlaying: isVideoPlayingRef.current,
            },
            "debug",
            "useVideoSegments",
          );
        }
        return;
      }

      // Auto-start segment if none exists (fallback for missed onStart event)
      // But only if user has interacted and video is actually playing
      const currentSegmentInfo =
        segmentTrackerRef.current.getCurrentSegmentInfo();
      if (
        !currentSegmentInfo &&
        currentTime > 0 &&
        hasUserInteractedRef.current
      ) {
        log(
          `Auto-starting segment at ${currentTime.toFixed(2)}s (fallback for missed onStart)`,
          {
            currentTime,
            isVideoPlaying: isVideoPlayingRef.current,
            hasUserInteracted: hasUserInteractedRef.current,
          },
          "info",
          "useVideoSegments",
        );
        segmentTrackerRef.current.startSegment(currentTime);
      } else if (
        !currentSegmentInfo &&
        currentTime > 0 &&
        !hasUserInteractedRef.current
      ) {
        log(
          `Skipping auto-start segment at ${currentTime.toFixed(2)}s (no user interaction yet)`,
          {
            currentTime,
            isVideoPlaying: isVideoPlayingRef.current,
            hasUserInteracted: hasUserInteractedRef.current,
          },
          "debug",
          "useVideoSegments",
        );
      }

      // Only log every 5 seconds to reduce noise
      if (Math.floor(currentTime) % 5 === 0 && currentTime > 0) {
        log(
          `Hook: onTimeUpdate called with ${currentTime.toFixed(2)}s`,
          {
            currentTime,
            isVideoPlaying: isVideoPlayingRef.current,
          },
          "debug",
          "useVideoSegments",
        );
        // Also log current segment info for debugging
        const segmentInfo = segmentTrackerRef.current.getCurrentSegmentInfo();
        if (segmentInfo) {
          log(
            `Current segment: start=${segmentInfo.start.toFixed(2)}, duration=${segmentInfo.duration.toFixed(2)}`,
            { segment: segmentInfo },
            "debug",
            "useVideoSegments",
          );
        } else {
          log(`No current segment exists`, null, "debug", "useVideoSegments");
        }
      }
      segmentTrackerRef.current.updateSegment(currentTime);
    },
    [log],
  );

  const onSeeking = useCallback(
    (fromTime: number, toTime: number) => {
      if (!segmentTrackerRef.current) return;

      // Ignore seeking events during component initialization (no user interaction yet)
      if (!hasUserInteractedRef.current) {
        log(
          `Ignoring seeking event during initialization: ${fromTime.toFixed(2)}s to ${toTime.toFixed(2)}s`,
          { fromTime, toTime, hasUserInteracted: false },
          "debug",
          "useVideoSegments",
        );
        return;
      }

      log(
        `Hook: onSeeking called from ${fromTime.toFixed(2)}s to ${toTime.toFixed(2)}s`,
        { fromTime, toTime, hasUserInteracted: true },
        "info",
        "useVideoSegments",
      );
      segmentTrackerRef.current.onSeek(fromTime, toTime);
      const segments = segmentTrackerRef.current.flushSegments();
      if (segments.length) void trackVideoSegments(segments);
      segmentTrackerRef.current.startSegment(toTime);
    },
    [log, trackVideoSegments],
  );

  const onSeeked = useCallback(
    (toTime: number) => {
      // onSeeked is handled by onSeeking, but we can add additional logic here if needed
      log("Seeked to:", { toTime }, "debug", "useVideoSegments");
    },
    [log],
  );

  const onPlay = useCallback(
    (currentTime: number) => {
      if (!segmentTrackerRef.current) return;

      // Mark that user has interacted (clicked play)
      hasUserInteractedRef.current = true;
      isVideoPlayingRef.current = true;

      log(
        `Hook: onPlay called - video is now playing at ${currentTime.toFixed(2)}s`,
        {
          currentTime,
          hasUserInteracted: hasUserInteractedRef.current,
        },
        "info",
        "useVideoSegments",
      );
      segmentTrackerRef.current.onPlay(currentTime);
    },
    [log],
  );

  const onPause = useCallback(
    (currentTime: number) => {
      if (!segmentTrackerRef.current) return;
      isVideoPlayingRef.current = false;
      log(
        `Hook: onPause called - video is now paused at ${currentTime.toFixed(2)}s`,
        { currentTime },
        "info",
        "useVideoSegments",
      );
      segmentTrackerRef.current.onPause(currentTime);
      const segments = segmentTrackerRef.current.flushSegments();
      if (segments.length) void trackVideoSegments(segments);
    },
    [log, trackVideoSegments],
  );

  const onStart = useCallback(
    (currentTime: number) => {
      if (!segmentTrackerRef.current) {
        log(
          `Hook: onStart called but segmentTrackerRef.current is null at time ${currentTime.toFixed(2)}s`,
          null,
          "warn",
          "useVideoSegments",
        );
        return;
      }

      // Mark that user has interacted (clicked play)
      hasUserInteractedRef.current = true;
      isVideoPlayingRef.current = true;

      log(
        `Hook: onStart called with ${currentTime.toFixed(2)}s - starting segment`,
        {
          currentTime,
          isVideoPlaying: isVideoPlayingRef.current,
          hasUserInteracted: hasUserInteractedRef.current,
          existingSegment: segmentTrackerRef.current.getCurrentSegmentInfo(),
        },
        "info",
        "useVideoSegments",
      );
      segmentTrackerRef.current.startSegment(currentTime);
    },
    [log],
  );

  const onEnd = useCallback(() => {
    if (!segmentTrackerRef.current) return;
    log("Hook: onEnd called", null, "info", "useVideoSegments");
    segmentTrackerRef.current.onEnd();

    // Flush remaining segments when video ends
    const remainingSegments = segmentTrackerRef.current.flushSegments();
    if (remainingSegments.length > 0) {
      log(
        `Video ended, flushing ${remainingSegments.length} remaining segments`,
        { segments: remainingSegments },
        "info",
        "useVideoSegments",
      );
      trackVideoSegments(remainingSegments);
    }
  }, [trackVideoSegments, log]);

  // Flush completed segments immediately when they're ready
  const flushCompletedSegments = useCallback(() => {
    if (!segmentTrackerRef.current) return;

    const completedSegments =
      segmentTrackerRef.current.flushCompletedSegments();
    if (completedSegments.length > 0) {
      log(
        `Flushing ${completedSegments.length} completed segments immediately`,
        { segments: completedSegments },
        "info",
        "useVideoSegments",
      );
      trackVideoSegments(completedSegments);
    }
  }, [trackVideoSegments, log]);

  return {
    trackVideoSegments,
    flushSegments,
    getTotalWatchedTime,
    getCompletionPercentage,
    getCurrentSegmentInfo,
    onTimeUpdate,
    onSeeking,
    onSeeked,
    onPlay,
    onPause,
    onStart,
    onEnd,
  };
}
